import React, { useRef, useEffect, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize, RotateCcw, Lock, Loader2 } from 'lucide-react';
import { progressAPI } from '../../lib/api';
import { toast } from 'sonner';

const BunnyVideoPlayer = ({
  videoUrl,
  lessonId,
  onProgressUpdate,
  restrictSeek = false,
  className = ""
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const maxWatchedRef = useRef(0);
  const lastSaveRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [playedPercent, setPlayedPercent] = useState(0);
  const [watchedPercent, setWatchedPercent] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [playError, setPlayError] = useState(null);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Reset play error when the video source changes
  useEffect(() => {
    setPlayError(null);
  }, [videoUrl]);

  // Track fullscreen state (Esc handled natively by the browser)
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Load saved progress (resume + max watched position)
  useEffect(() => {
    if (!lessonId) return;
    let mounted = true;
    (async () => {
      try {
        const res = await progressAPI.getLessonProgress(lessonId);
        if (!mounted || !res.data) return;
        const savedMax = res.data.video_watched_seconds || 0;
        const savedPct = Math.min(100, res.data.progress_percentage || 0);
        const lastPos = res.data.video_last_position || 0;
        maxWatchedRef.current = savedMax;
        const video = videoRef.current;
        if (!video) return;
        const applyResume = () => {
          const dur = video.duration || 0;
          if (!dur) return;
          // Rebuild the exact watched position from BOTH saved values:
          // - percentage is exact; seconds are floored (and may be corrupted > duration)
          const fromSeconds = savedMax > dur ? 0 : savedMax;
          const fromPct = (savedPct / 100) * dur;
          maxWatchedRef.current = Math.min(dur, Math.max(fromSeconds, fromPct));
          setWatchedPercent(Math.min(100, (maxWatchedRef.current / dur) * 100));
          // Completed lesson: restart from the beginning; otherwise resume where the user left off
          if (savedPct < 99.5) {
            const target = Math.min(lastPos, maxWatchedRef.current, Math.max(0, dur - 0.1));
            if (target > 0) video.currentTime = target;
          }
        };
        if (video.readyState >= 1) applyResume();
        else video.addEventListener('loadedmetadata', applyResume, { once: true });
      } catch (e) { /* no saved progress */ }
    })();
    return () => { mounted = false; };
  }, [lessonId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const current = video.currentTime;
      const total = video.duration;
      if (!total || total <= 0) return;

      // Track max continuously-watched position (only advance while not seeking)
      if (!video.seeking && current <= maxWatchedRef.current + 1.5) {
        maxWatchedRef.current = Math.max(maxWatchedRef.current, current);
      }

      setCurrentTime(current);
      setPlayedPercent((current / total) * 100);
      const wp = Math.min(100, (maxWatchedRef.current / total) * 100);
      setWatchedPercent(wp);

      // Save every 10 seconds of playback
      if (current - lastSaveRef.current >= 10 || current < lastSaveRef.current) {
        lastSaveRef.current = current;
        saveProgress();
      }
    };

    const handleSeeking = () => {
      if (!restrictSeek) return;
      if (video.currentTime > maxWatchedRef.current + 1.5) {
        video.currentTime = maxWatchedRef.current;
        toast.info('Avance rapide désactivée — vous pouvez seulement revenir en arrière', { id: 'no-skip' });
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      if (video.duration && maxWatchedRef.current > video.duration) {
        maxWatchedRef.current = video.duration;
      }
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => { setIsPlaying(false); saveProgress(); };
    const handleWaiting = () => setIsBuffering(true);
    const handleCanPlay = () => setIsBuffering(false);
    const handlePlaying = () => setIsBuffering(false);
    const handleError = () => setPlayError(video.error?.message || 'Erreur de lecture vidéo');
    const handleEnded = () => {
      setIsPlaying(false);
      maxWatchedRef.current = video.duration;
      setWatchedPercent(100);
      saveProgress();
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('error', handleError);
    };
  }, [restrictSeek]);

  const saveProgress = async () => {
    const video = videoRef.current;
    if (!lessonId || !video || !video.duration) return;
    const percentage = Math.min(100, (maxWatchedRef.current / video.duration) * 100);
    try {
      await progressAPI.updateLessonProgress(lessonId, {
        progress_percentage: percentage,
        video_watched_seconds: Math.floor(maxWatchedRef.current),
        video_last_position: Math.floor(video.currentTime)
      });
      if (onProgressUpdate) onProgressUpdate(percentage);
    } catch (err) {
      console.error('Failed to save video progress:', err);
    }
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(err => {
        console.error('Lecture impossible:', err);
        setPlayError(err.message || 'Impossible de lire la vidéo');
      });
    } else {
      video.pause();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    let target = pos * duration;
    if (restrictSeek && target > maxWatchedRef.current) {
      target = maxWatchedRef.current;
      toast.info('Avance rapide désactivée — vous pouvez seulement revenir en arrière', { id: 'no-skip' });
    }
    video.currentTime = target;
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else container.requestFullscreen();
  };

  const restart = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.play().catch(err => {
      console.error('Lecture impossible:', err);
      setPlayError(err.message || 'Impossible de lire la vidéo');
    });
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!videoUrl) {
    return (
      <div className="aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
        <p className="text-gray-400">Aucune vidéo disponible</p>
      </div>
    );
  }

  const watchedBarPercent = duration > 0 ? Math.min(100, (maxWatchedRef.current / duration) * 100) : 0;

  return (
    <div
      ref={containerRef}
      className={`relative aspect-video bg-black rounded-lg overflow-hidden group ${className}`}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(!isPlaying)}
      data-testid="bunny-video-player"
    >
      <video
        ref={videoRef}
        src={videoUrl}
        preload="metadata"
        className="w-full h-full object-contain"
        onClick={togglePlay}
        playsInline
      />

      {/* Buffering spinner */}
      {isBuffering && !playError && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="video-buffering">
          <Loader2 className="w-12 h-12 text-white animate-spin" />
        </div>
      )}

      {/* Play error banner */}
      {playError && (
        <div className="absolute bottom-20 left-4 right-4 bg-red-600/95 text-white rounded-lg px-4 py-3 flex items-center justify-between gap-3 z-10" data-testid="video-error-banner">
          <span className="text-sm truncate">{playError}</span>
          <button
            onClick={() => { setPlayError(null); togglePlay(); }}
            className="flex-shrink-0 bg-white/20 hover:bg-white/30 rounded px-3 py-1 text-sm font-medium transition-colors"
            data-testid="video-retry-btn"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Controls Overlay */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Progress Bar */}
        <div
          className="w-full h-1.5 bg-gray-600 rounded-full cursor-pointer mb-3 relative group/progress"
          onClick={handleSeek}
        >
          {/* Watched (unlocked) zone */}
          <div
            className="absolute top-0 left-0 h-full bg-emerald-900/80 rounded-full"
            style={{ width: `${watchedBarPercent}%` }}
          />
          {/* Current position */}
          <div
            className="absolute top-0 left-0 h-full bg-emerald-500 rounded-full"
            style={{ width: `${playedPercent}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity" />
          </div>
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={togglePlay}
              className="text-white hover:text-emerald-400 transition-colors"
              data-testid="video-play-btn"
            >
              {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
            </button>

            <button
              onClick={restart}
              className="text-white hover:text-emerald-400 transition-colors"
              data-testid="video-restart-btn"
            >
              <RotateCcw className="w-5 h-5" />
            </button>

            <button
              onClick={toggleMute}
              className="text-white hover:text-emerald-400 transition-colors"
              data-testid="video-mute-btn"
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>

            <span className="text-white text-sm">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div className="flex items-center space-x-3">
            {restrictSeek && (
              <span className="flex items-center gap-1 text-amber-300/90 text-xs" title="Avance rapide désactivée">
                <Lock className="w-3 h-3" />
              </span>
            )}
            <span className="text-emerald-400 text-sm font-medium" data-testid="video-watched-percent">
              {Math.round(watchedPercent)}%
            </span>

            <button
              onClick={toggleFullscreen}
              className="text-white hover:text-emerald-400 transition-colors"
              data-testid="video-fullscreen-btn"
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Play Button Overlay (when paused) */}
      {!isPlaying && (
        <div
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={togglePlay}
        >
          <div className="w-16 h-16 bg-emerald-600/90 rounded-full flex items-center justify-center hover:bg-emerald-500 transition-colors">
            <Play className="w-8 h-8 text-white ml-1" />
          </div>
        </div>
      )}
    </div>
  );
};

export default BunnyVideoPlayer;
